import { useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { getArticleBySlug, getRelatedArticles, AUTHORS, CATEGORIES, canonicalFor } from '@/content/articles';

const LOGO_URL       = "/logo-wordmark.png";
const CANONICAL_HOST = "https://meettokeep.com";
const ORG_ID         = `${CANONICAL_HOST}/#organization`;

// -------------------------------------------------------------
// Markdown-lite renderer — H2, H3, paragraphs, lists, blockquote, links.
// Deliberately minimal; article authors don't need Markdown power.
// -------------------------------------------------------------
function renderInline(text) {
  const parts = [];
  const linkRe = /\[([^\]]+)\]\(([^)]+)\)/g;
  let last = 0, m;
  let key = 0;
  while ((m = linkRe.exec(text)) !== null) {
    if (m.index > last) parts.push(text.slice(last, m.index));
    parts.push(<a key={`l${key++}`} href={m[2]} className="text-[#1A1A1A] underline decoration-zinc-300 underline-offset-4 hover:decoration-zinc-800">{m[1]}</a>);
    last = m.index + m[0].length;
  }
  if (last < text.length) parts.push(text.slice(last));
  // Bold **x** and italic *x*
  return parts.flatMap((p, i) => {
    if (typeof p !== 'string') return [p];
    const out = [];
    const rx = /(\*\*[^*]+\*\*|\*[^*]+\*)/g;
    let cur = 0, mm;
    let k = 0;
    while ((mm = rx.exec(p)) !== null) {
      if (mm.index > cur) out.push(p.slice(cur, mm.index));
      if (mm[0].startsWith('**')) out.push(<strong key={`b${i}-${k++}`}>{mm[0].slice(2, -2)}</strong>);
      else out.push(<em key={`i${i}-${k++}`}>{mm[0].slice(1, -1)}</em>);
      cur = mm.index + mm[0].length;
    }
    if (cur < p.length) out.push(p.slice(cur));
    return out;
  });
}

function renderMarkdown(md) {
  if (!md) return null;
  const blocks = md.split(/\n{2,}/);
  const nodes = [];
  let listBuf = null;
  const flushList = () => {
    if (listBuf) {
      nodes.push(
        <ul key={`ul-${nodes.length}`} className="list-disc pl-6 space-y-2 my-4 text-zinc-700 leading-relaxed">
          {listBuf.map((li, i) => <li key={i}>{renderInline(li)}</li>)}
        </ul>
      );
      listBuf = null;
    }
  };
  blocks.forEach((block, idx) => {
    const t = block.trim();
    if (!t) return;
    if (t.startsWith('### ')) { flushList(); nodes.push(<h3 key={idx} className="text-xl font-semibold mt-8 mb-3 text-[#1A1A1A]">{renderInline(t.slice(4))}</h3>); return; }
    if (t.startsWith('## '))  { flushList(); nodes.push(<h2 key={idx} className="text-2xl md:text-3xl font-semibold mt-12 mb-4 text-[#1A1A1A] tracking-tight">{renderInline(t.slice(3))}</h2>); return; }
    if (t.startsWith('> '))   { flushList(); nodes.push(<blockquote key={idx} className="my-6 p-5 rounded-2xl border-l-2 border-[#1A1A1A] bg-zinc-50/60 text-[#1A1A1A] leading-relaxed">{renderInline(t.slice(2))}</blockquote>); return; }
    if (t.match(/^- /m)) {
      const items = t.split(/\n- /).map(s => s.replace(/^- /, ''));
      listBuf = (listBuf || []).concat(items);
      return;
    }
    flushList();
    nodes.push(<p key={idx} className="my-4 text-zinc-700 leading-[1.8]">{renderInline(t)}</p>);
  });
  flushList();
  return nodes;
}

// -------------------------------------------------------------
// Metadata + JSON-LD injection
// -------------------------------------------------------------
function useArticleHead(article, author) {
  useEffect(() => {
    if (!article) return;
    const prevTitle = document.title;
    document.title = `${article.seoTitle || article.title} | Meet to Keep`;

    const setMeta = (n, c, attr = 'name') => {
      let el = document.querySelector(`meta[${attr}="${n}"]`);
      if (!el) { el = document.createElement('meta'); el.setAttribute(attr, n); document.head.appendChild(el); }
      el.setAttribute('content', c);
      return el;
    };

    const canonical = canonicalFor(article.slug);
    const ogImg = article.featuredImage?.src ? `${CANONICAL_HOST}${article.featuredImage.src}` : `${CANONICAL_HOST}${LOGO_URL}`;

    setMeta('description', article.metaDescription);
    // Drafts MUST NOT be indexed.
    setMeta('robots', article.published ? 'index, follow' : 'noindex, nofollow');
    setMeta('og:title', article.seoTitle || article.title, 'property');
    setMeta('og:description', article.metaDescription, 'property');
    setMeta('og:url', canonical, 'property');
    setMeta('og:type', 'article', 'property');
    setMeta('og:image', ogImg, 'property');
    setMeta('twitter:card', 'summary_large_image');
    setMeta('twitter:title', article.seoTitle || article.title);
    setMeta('twitter:description', article.metaDescription);
    setMeta('twitter:image', ogImg);

    let link = document.querySelector('link[rel="canonical"]');
    const hadLink = !!link;
    const prevHref = link?.getAttribute('href');
    if (!link) { link = document.createElement('link'); link.setAttribute('rel', 'canonical'); document.head.appendChild(link); }
    link.setAttribute('href', canonical);

    // JSON-LD: BlogPosting + BreadcrumbList. Reuses Organization @id.
    // Drafts get NO JSON-LD (no publication signal until reviewed).
    let jsonLd = null;
    if (article.published) {
      const authorNode = {
        "@type": "Person",
        "name":  author.name,
        ...(author.url ? { "url": author.url } : {}),
      };
      const graph = {
        "@context": "https://schema.org",
        "@graph": [
          {
            "@type":            "BlogPosting",
            "@id":              `${canonical}#article`,
            "mainEntityOfPage": { "@id": canonical },
            "headline":         article.title,
            "description":      article.metaDescription,
            "datePublished":    article.datePublished,
            ...(article.dateModified ? { "dateModified": article.dateModified } : {}),
            "author":           authorNode,
            "publisher":        { "@id": ORG_ID },
            ...(article.featuredImage?.src ? { "image": ogImg } : {}),
            "inLanguage":       "en-US",
            "articleSection":   CATEGORIES[article.category]?.label || "Article",
          },
          {
            "@type":            "BreadcrumbList",
            "@id":              `${canonical}#breadcrumbs`,
            "itemListElement": [
              { "@type": "ListItem", "position": 1, "name": "Home",                          "item": CANONICAL_HOST + "/" },
              { "@type": "ListItem", "position": 2, "name": "Dating & Relationship Advice",  "item": CANONICAL_HOST + "/articles" },
              { "@type": "ListItem", "position": 3, "name": article.title,                   "item": canonical },
            ],
          },
        ],
      };
      jsonLd = document.createElement('script');
      jsonLd.type = 'application/ld+json';
      jsonLd.id = 'article-jsonld';
      jsonLd.textContent = JSON.stringify(graph);
      document.head.appendChild(jsonLd);
    }

    return () => {
      document.title = prevTitle;
      if (hadLink && prevHref) link.setAttribute('href', prevHref);
      else if (!hadLink && link) link.remove();
      if (jsonLd) jsonLd.remove();
    };
  }, [article, author]);
}

export default function Article() {
  const { slug } = useParams();
  const navigate = useNavigate();
  const article = getArticleBySlug(slug);
  const author = article ? (AUTHORS[article.author] || AUTHORS.editorial) : null;
  useArticleHead(article, author);

  if (!article) {
    return (
      <div className="min-h-screen bg-white flex items-center justify-center px-6">
        <div className="text-center">
          <h1 className="text-2xl font-semibold text-[#1A1A1A] mb-2">Article not found</h1>
          <p className="text-sm text-zinc-500 mb-6">The article you&rsquo;re looking for doesn&rsquo;t exist or has moved.</p>
          <a href="/articles" className="text-sm underline decoration-zinc-300 underline-offset-4 hover:decoration-zinc-800">
            Browse all articles
          </a>
        </div>
      </div>
    );
  }

  const cat = CATEGORIES[article.category]?.label || 'Article';
  const related = getRelatedArticles(article, 3);
  const publishedDate = article.datePublished ? new Date(article.datePublished).toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' }) : null;
  const updatedDate = article.dateModified ? new Date(article.dateModified).toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' }) : null;

  return (
    <div className="min-h-screen bg-white text-[#1A1A1A]" data-testid={`article-page-${slug}`}>
      <nav className="fixed top-0 left-0 right-0 z-50 px-6 py-4 flex items-center justify-between bg-white/90 backdrop-blur-xl border-b border-black/[0.04]">
        <button onClick={() => navigate('/')} aria-label="Meet to Keep home">
          <img src={LOGO_URL} alt="Meet to Keep" className="h-10 md:h-12 w-auto object-contain" />
        </button>
        <button onClick={() => navigate('/auth')}
          className="text-sm font-medium text-zinc-500 hover:text-[#1A1A1A] px-4 py-2 rounded-full border border-black/[0.08] hover:border-black/[0.15]">
          Sign in
        </button>
      </nav>

      <main className="pt-24 pb-20">
        {!article.published && (
          <div className="max-w-3xl mx-auto px-6 mb-4">
            <div className="rounded-xl bg-amber-100 border border-amber-300 px-4 py-3 text-xs font-semibold uppercase tracking-wider text-amber-900" data-testid="article-draft-banner">
              Draft · Not indexed · Not linked from hub
            </div>
          </div>
        )}

        <article className="max-w-3xl mx-auto px-6">
          <nav aria-label="Breadcrumb" className="mb-6 text-xs text-zinc-500">
            <a href="/" className="hover:text-[#1A1A1A]">Home</a>
            <span className="mx-2">→</span>
            <a href="/articles" className="hover:text-[#1A1A1A]">Dating &amp; Relationship Advice</a>
            <span className="mx-2">→</span>
            <span aria-current="page">{article.title}</span>
          </nav>

          <p className="text-[11px] font-semibold uppercase tracking-[0.14em] text-zinc-500 mb-3">{cat}</p>
          <h1 className="text-3xl md:text-5xl font-semibold tracking-tight leading-[1.1] mb-4" data-testid="article-h1">
            {article.title}
          </h1>
          <div className="flex flex-wrap items-center gap-x-4 gap-y-1 text-sm text-zinc-500 mb-8">
            <span data-testid="article-author">{author.name}</span>
            {publishedDate && <span>Published: {publishedDate}</span>}
            {updatedDate && <span>Updated: {updatedDate}</span>}
            <span>{article.readingTimeMin} min read</span>
          </div>

          {article.featuredImage?.src && (
            <div className="aspect-[16/9] rounded-2xl overflow-hidden bg-zinc-100 mb-10">
              <img src={article.featuredImage.src} alt={article.featuredImage.alt || article.title} loading="lazy" className="w-full h-full object-cover" />
            </div>
          )}

          <div className="prose prose-zinc max-w-none" data-testid="article-body">
            {article.content ? renderMarkdown(article.content) : (
              <p className="text-zinc-400 italic">Content pending review.</p>
            )}
          </div>

          <hr className="my-12 border-black/[0.08]" />

          <div className="rounded-2xl bg-zinc-50/70 border border-black/[0.06] p-6 md:p-8" data-testid="article-cta">
            <p className="text-lg font-semibold text-[#1A1A1A] mb-2">Ready to date with intention?</p>
            <p className="text-sm text-zinc-600 leading-relaxed mb-4">
              Meet to Keep is built for people looking for meaningful, relationship-minded connections — not endless swiping.
            </p>
            <a href="/" className="inline-flex items-center gap-2 px-5 py-2.5 bg-[#1A1A1A] hover:bg-black text-white rounded-full text-sm font-semibold">
              Discover Meet to Keep
            </a>
          </div>
        </article>

        {related.length > 0 && (
          <section className="max-w-5xl mx-auto px-6 mt-16" aria-labelledby="related-title">
            <h2 id="related-title" className="text-2xl font-semibold mb-6">Related Articles</h2>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              {related.map((r) => (
                <a key={r.slug} href={`/articles/${r.slug}`} className="rounded-2xl border border-black/[0.06] p-5 hover:border-black/[0.20] transition-colors bg-white">
                  <p className="text-[10px] font-semibold uppercase tracking-[0.14em] text-zinc-500 mb-2">
                    {CATEGORIES[r.category]?.label || 'Article'}
                  </p>
                  <h3 className="text-base font-semibold text-[#1A1A1A] leading-snug mb-2">{r.title}</h3>
                  <p className="text-xs text-zinc-500">{r.readingTimeMin} min read</p>
                </a>
              ))}
            </div>
          </section>
        )}
      </main>
    </div>
  );
}
